import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import type { WalletBalance } from '../../types/wallet'
import { MINTS, SYMBOLS, jupiterQuote, jupiterSwap, type JupiterQuote } from '../../api/trade'
import { deserializeV0 } from '../../lib/tx'
import { useAdapterSignAndSubmit } from '../../lib/walletAdapter'
import { findBalance, formatBalance, formatUsdShort } from '../../hooks/useWalletBalance'
import { useSplBalance } from '../../hooks/useSplBalance'
import TokenIcon from '../common/TokenIcon'
import TradeResultBanner, { type TradeResult } from './TradeResultBanner'

// Cushion for stake (SOL→jupSOL) — priority fee + jupSOL ATA rent (~0.00204
// SOL) when the user has no jupSOL ATA yet. 0.005 wasn't enough on a fresh
// wallet; 0.01 leaves room for both.
const SOL_GAS_CUSHION = 0.01
// SOL the wallet must keep on hand for tx fees on the unstake leg
// (jupSOL → SOL). The output side already credits SOL, but the tx itself is
// signed against current SOL.
const MIN_SOL_FOR_UNSTAKE = 0.005

function parseSol(raw: string): bigint | null {
  if (!raw || !/^[0-9]*\.?[0-9]*$/.test(raw)) return null
  const [whole, frac = ''] = raw.split('.')
  const padded = (frac + '000000000').slice(0, 9)
  try { return BigInt((whole || '0') + padded) || null } catch { return null }
}

function lamportsToSol(raw: string | number): string {
  const n = typeof raw === 'string' ? BigInt(raw) : BigInt(Math.floor(Number(raw)))
  const s = n.toString().padStart(10, '0')
  const head = s.slice(0, -9)
  const tail = s.slice(-9).replace(/0+$/, '')
  return tail ? `${head}.${tail}` : head
}

interface Props {
  balances: WalletBalance[] | null
  defaultAmount?: string
}

export default function StakeForm({ balances, defaultAmount }: Props) {
  const { portfolioId } = useAuth()
  const { signAndSubmit, publicKey, connected, friendlyTxError } = useAdapterSignAndSubmit()

  const [amount, setAmount] = useState(defaultAmount ?? '')
  const [quote, setQuote]   = useState<JupiterQuote | null>(null)
  const [quoting, setQuoting] = useState(false)
  const [result, setResult] = useState<TradeResult>({ kind: 'idle' })
  const [direction, setDirection] = useState<'stake' | 'unstake'>('stake')

  const inputMint  = direction === 'stake' ? MINTS.SOL    : MINTS.jupSOL
  const outputMint = direction === 'stake' ? MINTS.jupSOL : MINTS.SOL
  const rawInput = useMemo(() => parseSol(amount), [amount])
  const inBalance  = findBalance(balances, inputMint)    // merged display only
  const outBalance = findBalance(balances, outputMint)

  // SOL preflight for the unstake leg — the input is jupSOL but the tx fees
  // still come out of the user's native SOL. (Stake uses the same SOL pool as
  // the input itself, gated by SOL_GAS_CUSHION inside onMax.)
  const walletSol      = findBalance(balances, MINTS.SOL)
  const solBalanceNum  = parseFloat(walletSol?.amount ?? '0')
  const insufficientSolForFees = direction === 'unstake' && balances != null && solBalanceNum < MIN_SOL_FOR_UNSTAKE

  // Authoritative RPC-direct wallet balance for the input token. Native SOL
  // goes through connection.getBalance; jupSOL through the SPL ATA read.
  const splIn = useSplBalance(publicKey, inputMint)

  const fetchQuote = useCallback(async () => {
    if (!rawInput || rawInput <= 0n) { setQuote(null); return }
    setQuoting(true)
    try {
      const q = await jupiterQuote({ inputMint, outputMint, amount: rawInput.toString(), slippageBps: 30 })
      setQuote(q)
    } catch (e) {
      setQuote(null)
      setResult({ kind: 'error', message: friendlyTxError(e) })
    } finally { setQuoting(false) }
  }, [rawInput, inputMint, outputMint, friendlyTxError])

  useEffect(() => {
    const h = setTimeout(() => { fetchQuote() }, 400)
    return () => clearTimeout(h)
  }, [fetchQuote])

  const submit = async () => {
    if (!quote) return
    setResult({ kind: 'pending', label: 'Building stake transaction…' })
    try {
      // Use the auth-session wallet pubkey as the signer when the wallet-adapter
      // hasn't finished its silent autoConnect yet — signAndSubmit will retry
      // the connect itself before signing.
      const signer = publicKey?.toBase58() ?? portfolioId
      if (!signer) throw new Error('Wallet not connected — open Phantom/Solflare and retry')
      if (publicKey && portfolioId && publicKey.toBase58() !== portfolioId) {
        throw new Error('Connected wallet does not match your session')
      }
      const { swapTransaction, lastValidBlockHeight } = await jupiterSwap({ userPublicKey: signer, quoteResponse: quote })
      setResult({ kind: 'pending', label: 'Waiting for wallet signature…' })
      const tx = deserializeV0(swapTransaction)
      const submitted = await signAndSubmit(tx, { lastValidBlockHeight })
      const outReadable = lamportsToSol(quote.outAmount)
      const inReadable  = lamportsToSol(quote.inAmount)
      const summary = direction === 'stake'
        ? `Staked ${inReadable} SOL → ${outReadable} jupSOL`
        : `Unstaked ${inReadable} jupSOL → ${outReadable} SOL`
      setResult({ kind: 'success', signature: submitted.signature, summary })
      setAmount('')
      setQuote(null)
    } catch (e) {
      setResult({ kind: 'error', message: friendlyTxError(e) })
    }
  }

  const onMax = () => {
    if (splIn.raw.isZero()) return
    let max = Number(splIn.decimal) || 0
    if (inputMint === MINTS.SOL) max = Math.max(0, max - SOL_GAS_CUSHION)
    setAmount(max.toString())
  }

  const outReadable = quote ? lamportsToSol(quote.outAmount) : '—'
  const rate = quote && rawInput && rawInput > 0n
    ? (Number(BigInt(quote.outAmount)) / Number(rawInput)).toFixed(6)
    : '—'

  return (
    <div className="space-y-3">
      <div className="flex gap-1 rounded-lg bg-surface-hover p-1">
        {(['stake', 'unstake'] as const).map(d => (
          <button key={d}
            onClick={() => { setDirection(d); setAmount(''); setQuote(null) }}
            className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold ${direction === d ? 'bg-accent-purple/25 text-ink' : 'text-ink-muted'}`}
          >
            {d === 'stake' ? (
              <>
                <TokenIcon symbol="SOL" mint={MINTS.SOL} size={14} />
                <span>SOL → jupSOL</span>
                <TokenIcon symbol="jupSOL" mint={MINTS.jupSOL} size={14} />
              </>
            ) : (
              <>
                <TokenIcon symbol="jupSOL" mint={MINTS.jupSOL} size={14} />
                <span>jupSOL → SOL</span>
                <TokenIcon symbol="SOL" mint={MINTS.SOL} size={14} />
              </>
            )}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <BalanceCard mint={MINTS.SOL}    balance={direction === 'stake'   ? inBalance  : outBalance} />
        <BalanceCard mint={MINTS.jupSOL} balance={direction === 'unstake' ? inBalance  : outBalance} />
      </div>

      <div className="rounded-lg border border-fringe bg-surface-hover p-3">
        <div className="mb-1 flex items-center justify-between">
          <label className="text-[10px] uppercase tracking-wider text-gray-500">
            {direction === 'stake' ? 'Stake amount (SOL)' : 'Unstake amount (jupSOL)'}
          </label>
          <button
            type="button"
            onClick={onMax}
            disabled={splIn.loading || splIn.raw.isZero()}
            className="text-[10px] text-accent-purple hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
          >
            {splIn.loading
              ? 'MAX …'
              : splIn.raw.isZero()
                ? `MAX 0 ${SYMBOLS[inputMint] ?? ''}`
                : `MAX ${formatBalance(splIn.decimal, 4)}`}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <TokenIcon symbol={SYMBOLS[inputMint] ?? ''} mint={inputMint} size={18} />
          <input
            type="text"
            inputMode="decimal"
            placeholder="0.0"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            className="flex-1 bg-transparent text-lg font-semibold text-ink outline-none"
          />
        </div>
      </div>

      <div className="space-y-1 rounded-lg bg-surface-hover p-3 text-[11px]">
        <Row label={`You receive (${SYMBOLS[outputMint]})`} value={outReadable} />
        <Row label="Effective rate" value={rate} />
        <Row label="Slippage" value="0.30%" />
      </div>

      {insufficientSolForFees && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200 leading-relaxed">
          Wallet has only {solBalanceNum.toFixed(4)} SOL — unstaking still pays
          tx fees from native SOL. Top up at least {MIN_SOL_FOR_UNSTAKE} SOL and retry.
        </div>
      )}

      <button
        type="button"
        disabled={!quote || result.kind === 'pending' || insufficientSolForFees}
        onClick={submit}
        className="w-full rounded-lg bg-accent-purple py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-accent-purple/40"
      >
        {quoting
          ? 'Quoting…'
          : result.kind === 'pending' ? 'Working…'
          : insufficientSolForFees ? 'Top up SOL to unstake'
          : !connected ? (direction === 'stake' ? 'Connect & Stake' : 'Connect & Unstake')
          : direction === 'stake' ? 'Sign & Stake' : 'Sign & Unstake'}
      </button>

      <TradeResultBanner result={result} onDismiss={() => setResult({ kind: 'idle' })} />

      <p className="text-[10px] text-gray-500">
        Routes through Jupiter Swap — jupSOL ↔ SOL uses Sanctum LST liquidity and includes instant-unstake routes when available.
      </p>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-gray-500">
      <span>{label}</span>
      <span className="text-ink">{value}</span>
    </div>
  )
}

function BalanceCard({ mint, balance }: { mint: string; balance: ReturnType<typeof findBalance> }) {
  const sym = SYMBOLS[mint] ?? '?'
  return (
    <div className="flex items-center gap-2 rounded-lg bg-surface-hover p-2.5">
      <TokenIcon symbol={sym} mint={mint} size={20} />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-gray-500">{sym}</div>
        <div className="truncate text-sm font-semibold text-ink">
          {balance ? formatBalance(balance.amount) : '—'}
        </div>
        <div className="text-[10px] text-gray-500">
          {balance ? formatUsdShort(balance.usdValue) : '—'}
        </div>
      </div>
    </div>
  )
}
